import * as React from "react";

import { registerWorkspaceNode, getWorkspace } from "../../../services/workspacesApi";
import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import {
  isPersistedWorkspaceResourceId,
  workspacePersistNodeId,
  workspacePersistWorkspaceId,
} from "./workspaceOptimisticReport";
import { findUpstreamStrategyEntityId } from "./workspaceGraphUtils";
import { hydrateWorkspaceSimulationPolicies } from "./workspaceLineageHydrate";
import { WorkspacePolicyDrawerContent } from "./drawer/WorkspacePolicyDrawerContent";
import { WorkspaceReportDrawerContent } from "./drawer/WorkspaceReportDrawerContent";
import { WorkspaceSimulationDrawerContent } from "./drawer/WorkspaceSimulationDrawerContent";
import { WorkspaceStrategyDrawerContent } from "./drawer/WorkspaceStrategyDrawerContent";

export type WorkspaceNodeDrawerBodyProps = {
  node: WorkspaceNode;
  brokerAccountCode: string;
  brokerName?: string;
  workspaceId?: string;
  nodes?: WorkspaceNode[];
  edges?: WorkspaceEdge[];
  onClose?: () => void;
  onRefreshWorkspace?: () => Promise<void>;
};

async function ensureSimulationNodeOnCanvas(args: {
  scope: { brokerName: string; brokerAccountCode: string };
  workspaceId: string;
  parentNodeId: string;
  backtestId: string;
  nodes: WorkspaceNode[];
}): Promise<WorkspaceNode | null> {
  const entityId = args.backtestId.trim();
  if (!entityId) return null;

  const existing = args.nodes.find(
    (entry) =>
      (entry.type === "simulation" || entry.type === "combined_simulation") &&
      entry.entityId.trim() === entityId
  );
  if (existing) return existing;

  try {
    const registered = await registerWorkspaceNode(args.scope, args.workspaceId, {
      type: "simulation",
      entityId,
      parentNodeId: args.parentNodeId,
    });
    if (registered.node) return registered.node;
  } catch {
    // duplicate_node or soft-fail — resolve from a fresh workspace fetch below.
  }

  try {
    const detail = await getWorkspace(args.scope, args.workspaceId);
    return (
      detail.nodes.find(
        (entry) =>
          (entry.type === "simulation" || entry.type === "combined_simulation") &&
          entry.entityId.trim() === entityId
      ) ?? null
    );
  } catch {
    return null;
  }
}

export function WorkspaceNodeDrawerBody({
  node,
  brokerAccountCode,
  brokerName,
  workspaceId,
  nodes = [],
  edges = [],
  onClose,
  onRefreshWorkspace,
}: WorkspaceNodeDrawerBodyProps): React.ReactElement | null {
  const handleDeleted = React.useCallback(() => {
    onClose?.();
    void onRefreshWorkspace?.();
  }, [onClose, onRefreshWorkspace]);

  const upstreamStrategyId = React.useMemo(
    () => findUpstreamStrategyEntityId(node.id, nodes, edges),
    [edges, node.id, nodes]
  );

  const workspaceScope = React.useMemo(() => {
    const name = brokerName?.trim();
    const code = brokerAccountCode.trim();
    if (!name || !code) return null;
    return { brokerName: name, brokerAccountCode: code };
  }, [brokerAccountCode, brokerName]);

  /** Ensure policy versions for a backtest show as POLICY nodes under that simulation. */
  const hydratePoliciesForBacktest = React.useCallback(
    async (simulationNode: WorkspaceNode, backtestId: string, strategyId?: string | null) => {
      const wid =
        simulationNode.sourceWorkspaceId?.trim() ||
        workspaceId?.trim() ||
        "";
      if (
        !wid ||
        !workspaceScope ||
        (simulationNode.type !== "simulation" && simulationNode.type !== "combined_simulation")
      ) {
        return;
      }
      if (!isPersistedWorkspaceResourceId(wid)) return;
      const fromArg = strategyId?.trim() || "";
      const fromGraph = findUpstreamStrategyEntityId(simulationNode.id, nodes, edges) || "";
      const strategyIds = fromArg ? [fromArg] : fromGraph ? [fromGraph] : [];
      // Empty strategyIds is OK — hydrate resolves via the backtest/report link.
      await hydrateWorkspaceSimulationPolicies({
        scope: workspaceScope,
        workspaceId: wid,
        simulationNode,
        backtestId,
        ...(strategyIds.length > 0 ? { strategyIds } : {}),
      });
    },
    [edges, nodes, workspaceId, workspaceScope]
  );

  if (node.type === "strategy") {
    return (
      <WorkspaceStrategyDrawerContent
        strategyId={node.entityId}
        onDeleted={handleDeleted}
        onClose={onClose}
      />
    );
  }
  if (node.type === "report") {
    const attachWorkspaceId = workspaceId
      ? workspacePersistWorkspaceId(node, workspaceId)
      : "";
    const attachParentNodeId = workspacePersistNodeId(node);
    return (
      <WorkspaceReportDrawerContent
        reportId={node.entityId}
        workspaceSimulationContext={
          attachWorkspaceId &&
          isPersistedWorkspaceResourceId(attachWorkspaceId) &&
          isPersistedWorkspaceResourceId(attachParentNodeId)
            ? {
                workspaceId: attachWorkspaceId,
                parentNodeId: attachParentNodeId,
              }
            : undefined
        }
        resolveWorkspacePolicyAttach={(backtestId) => {
          const entityId = backtestId.trim();
          if (!entityId || !workspaceId) return null;
          const simNode = nodes.find(
            (entry) =>
              (entry.type === "simulation" || entry.type === "combined_simulation") &&
              entry.entityId.trim() === entityId
          );
          if (!simNode) return null;
          const wid = workspacePersistWorkspaceId(simNode, workspaceId);
          const parentId = workspacePersistNodeId(simNode);
          if (
            !isPersistedWorkspaceResourceId(wid) ||
            !isPersistedWorkspaceResourceId(parentId)
          ) {
            return null;
          }
          return { workspaceId: wid, parentNodeId: parentId };
        }}
        onWorkspaceSimulationSaved={async (backtestId) => {
          const entityId = backtestId.trim();
          if (
            workspaceScope &&
            isPersistedWorkspaceResourceId(attachWorkspaceId) &&
            isPersistedWorkspaceResourceId(attachParentNodeId) &&
            isPersistedWorkspaceResourceId(entityId)
          ) {
            await ensureSimulationNodeOnCanvas({
              scope: workspaceScope,
              workspaceId: attachWorkspaceId,
              parentNodeId: attachParentNodeId,
              backtestId: entityId,
              nodes,
            });
          }
          await onRefreshWorkspace?.();
        }}
        onStrategyVersionSaved={async (backtestId) => {
          const id = backtestId?.trim() ?? "";
          if (
            id &&
            workspaceScope &&
            isPersistedWorkspaceResourceId(attachWorkspaceId) &&
            isPersistedWorkspaceResourceId(attachParentNodeId)
          ) {
            const simNode = await ensureSimulationNodeOnCanvas({
              scope: workspaceScope,
              workspaceId: attachWorkspaceId,
              parentNodeId: attachParentNodeId,
              backtestId: id,
              nodes,
            });
            if (simNode) {
              await hydratePoliciesForBacktest(simNode, id, upstreamStrategyId);
            }
          }
          await onRefreshWorkspace?.();
        }}
        onDeleted={handleDeleted}
        onClose={onClose}
      />
    );
  }
  if (node.type === "simulation") {
    const attachWorkspaceId = workspaceId
      ? workspacePersistWorkspaceId(node, workspaceId)
      : "";
    const attachParentNodeId = workspacePersistNodeId(node);
    return (
      <WorkspaceSimulationDrawerHydrate
        node={node}
        brokerAccountCode={brokerAccountCode}
        prefillStrategyId={upstreamStrategyId}
        workspaceId={workspaceId}
        workspacePolicyContext={
          attachWorkspaceId &&
          isPersistedWorkspaceResourceId(attachWorkspaceId) &&
          isPersistedWorkspaceResourceId(attachParentNodeId)
            ? {
                workspaceId: attachWorkspaceId,
                parentNodeId: attachParentNodeId,
              }
            : undefined
        }
        hydratePoliciesForBacktest={hydratePoliciesForBacktest}
        onRefreshWorkspace={onRefreshWorkspace}
        onDeleted={handleDeleted}
        onClose={onClose}
      />
    );
  }
  if (node.type === "policy" || node.type === "combined_policy") {
    return (
      <WorkspacePolicyDrawerContent
        policyVersionId={node.entityId}
        nodeId={node.id}
        nodes={nodes}
        edges={edges}
        workspaceBrokerName={brokerName}
        workspaceBrokerAccountCode={brokerAccountCode}
        combinedMeta={
          node.type === "combined_policy"
            ? {
                adoptedReportId: node.meta?.adoptedReportId ?? null,
                sourcePolicyVersionIds: node.meta?.sourcePolicyVersionIds ?? null,
                title: node.meta?.title ?? null,
              }
            : undefined
        }
        onClose={onClose}
        onDeleted={handleDeleted}
        onAccountsSaved={onRefreshWorkspace}
      />
    );
  }
  if (node.type === "combined_simulation") {
    return (
      <WorkspaceSimulationDrawerContent
        backtestId={node.entityId}
        brokerAccountCode={brokerAccountCode}
        prefillStrategyId={upstreamStrategyId}
        workspacePolicyContext={
          workspaceId
            ? {
                workspaceId,
                parentNodeId: workspacePersistNodeId(node),
              }
            : undefined
        }
        onStrategyVersionSaved={async (backtestId) => {
          const id = backtestId?.trim() || node.entityId;
          await hydratePoliciesForBacktest(node, id, upstreamStrategyId);
          await onRefreshWorkspace?.();
        }}
        onSimulationUpdated={async () => {
          await onRefreshWorkspace?.();
        }}
        onDeleted={handleDeleted}
        onClose={onClose}
      />
    );
  }
  return (
    <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
      Account nodes represent the broker account root for this workspace.
    </p>
  );
}

/** Simulation drawer + one-shot policy hydrate so existing versions appear as POLICY nodes. */
function WorkspaceSimulationDrawerHydrate({
  node,
  brokerAccountCode,
  prefillStrategyId,
  workspaceId,
  workspacePolicyContext,
  hydratePoliciesForBacktest,
  onRefreshWorkspace,
  onDeleted,
  onClose,
}: {
  node: WorkspaceNode;
  brokerAccountCode: string;
  prefillStrategyId: string | null;
  workspaceId?: string;
  workspacePolicyContext?: import("../../../types/workspace").WorkspaceCreateContext;
  hydratePoliciesForBacktest: (
    simulationNode: WorkspaceNode,
    backtestId: string,
    strategyId?: string | null
  ) => Promise<void>;
  onRefreshWorkspace?: () => Promise<void>;
  onDeleted: () => void;
  onClose?: () => void;
}): React.ReactElement {
  const didHydrateRef = React.useRef(false);

  React.useEffect(() => {
    if (didHydrateRef.current || !workspaceId) return;
    didHydrateRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        await hydratePoliciesForBacktest(node, node.entityId, prefillStrategyId);
        if (!cancelled) await onRefreshWorkspace?.();
      } catch {
        // Soft-fail — drawer still shows simulation results.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydratePoliciesForBacktest, node, onRefreshWorkspace, prefillStrategyId, workspaceId]);

  return (
    <WorkspaceSimulationDrawerContent
      backtestId={node.entityId}
      brokerAccountCode={brokerAccountCode}
      prefillStrategyId={prefillStrategyId}
      workspacePolicyContext={workspacePolicyContext}
      onStrategyVersionSaved={async () => {
        await hydratePoliciesForBacktest(node, node.entityId, prefillStrategyId);
        await onRefreshWorkspace?.();
      }}
      onSimulationUpdated={async () => {
        await onRefreshWorkspace?.();
      }}
      onDeleted={onDeleted}
      onClose={onClose}
    />
  );
}
